<template>
    <ClientOnly>
        <ValidatedForm
            ref="form"
            :show-required-fields-message="true"
            :form-errors="formErrors"
            data-cy="custom-rollstock-request-form"
            @submit="onRollstockFormSubmit"
        >
            <DropdownInput
                v-model="materialId"
                name="material_id"
                label="Material type *"
                container-class="col-span-12"
                :choices="materials"
                :form-errors="formErrors"
                :submitting="submitting"
                :dropdown-config="{ label: 'option', valueProp: 'value' }"
                :use-native-menu="true"
            />

            <TextInput
                v-model="width"
                name="roll_width"
                type="number"
                step=".001"
                label="Roll Width *"
                help-text="Measured in inches"
                :submitting="submitting"
                :form-errors="formErrors"
                container-class="mt-9"
            />

            <TextInput
                v-model="length"
                name="impression_length"
                type="number"
                step=".001"
                label="Impression Length *"
                help-text="Measured in inches"
                :submitting="submitting"
                :form-errors="formErrors"
                container-class="mt-9"
            />

            <TextInput
                v-model="impressions"
                name="number_of_impressions"
                type="number"
                label="Number of impressions *"
                :submitting="submitting"
                :form-errors="formErrors"
                container-class="mt-9"
            />

            <div class="mb-2 mt-9 font-normal">SELECT AN UNWIND DIRECTION</div>
            <div class="mt-6 grid grid-cols-2 gap-6 lg:grid-cols-4">
                <ConfiguratorButtonImage
                    v-for="choice in unwinds"
                    :key="choice.value"
                    :label="choice.label"
                    :image-url="choice.imageUrl as string"
                    :selected="choice.value === unwind"
                    :errored="unwindDirectionError"
                    :tooltip="choice.tooltip"
                    aspect-class="aspect-auto"
                    image-classes="p-4 w-32 mx-auto"
                    width-class="w-full"
                    :data-cy="`unwind-${choice.value}`"
                    @click="onUnwindClick(choice.value)"
                />
            </div>

            <TextareaInput
                v-model="packagingComments"
                name="packaging_comments"
                label="What will you be packaging? *"
                :submitting="submitting"
                :form-errors="formErrors"
                container-class="mt-9"
                data-cy="packaging-comments"
            />

            <h3 class="mt-9">Enter your contact information</h3>

            <div class="mt-9 flex gap-5">
                <TextInput
                    v-model="company"
                    name="company_name"
                    type="text"
                    label="Company Name *"
                    :submitting="submitting"
                    :form-errors="formErrors"
                    container-class="w-2/3"
                />

                <TextInput
                    v-model="zipCode"
                    name="zip_code"
                    type="text"
                    label="Zip Code *"
                    :submitting="submitting"
                    :form-errors="formErrors"
                    container-class="w-1/3"
                />
            </div>

            <div class="mt-9 flex gap-5">
                <TextInput
                    v-model="firstName"
                    name="first_name"
                    type="text"
                    label="First Name *"
                    :submitting="submitting"
                    :form-errors="formErrors"
                    container-class="w-1/2"
                />

                <TextInput
                    v-model="lastName"
                    name="last_name"
                    type="text"
                    label="Last Name *"
                    :submitting="submitting"
                    :form-errors="formErrors"
                    container-class="w-1/2"
                />
            </div>

            <TextInput
                v-model="email"
                name="email"
                type="text"
                label="Email Address *"
                :submitting="submitting"
                :form-errors="formErrors"
                container-class="mt-9"
            />

            <TextInput
                v-model="phone"
                name="phone"
                type="tel"
                label="Phone Number *"
                :submitting="submitting"
                :form-errors="formErrors"
                container-class="mt-9"
            />

            <TextareaInput
                v-model="comments"
                name="comments"
                label="Any additional comments?"
                :submitting="submitting"
                :form-errors="formErrors"
                container-class="mt-9"
                data-cy="additional-comments"
            />

            <div
                v-if="recaptchaEnabled"
                class="g-recaptcha mt-4"
                :data-sitekey="recaptchaSiteKey"
            ></div>

            <LoadingButton
                type="submit"
                class="btn-primary mt-9 w-full"
                :loading="submitting"
                data-cy="rollstock-submit"
            >
                Submit Rollstock Request
            </LoadingButton>
        </ValidatedForm>
    </ClientOnly>
</template>

<script setup lang="ts">
import TextInput from '~/components/forms/TextInput.vue';
import ValidatedForm from '~/components/forms/ValidatedForm.vue';
import DropdownInput from '~/components/forms/DropdownInput.vue';
import ConfiguratorButtonImage from '~/components/configurator/ConfiguratorButtonImage.vue';
import TextareaInput from '~/components/forms/TextareaInput.vue';
import { scrollToId } from '~/utils/helpers';
import { alert } from '~/utils/alertModals';
import { sendDataLayerFormEvent } from '~/composables/useDataLayer';

useHead({
    script: [
        {
            src: 'https://www.google.com/recaptcha/api.js',
            async: true,
            defer: true,
        },
    ],
});

const { recaptchaEnabled, recaptchaSiteKey } = useRuntimeConfig().public;

const form = ref(null);

const materials = [
    { value: '1', option: 'Gloss Laminate' },
    { value: '2', option: 'Matte Laminate' },
    { value: '173', option: 'White Recyclable Matte Laminate' },
    { value: '3', option: 'Clear Gloss Laminate' },
    { value: '4', option: 'Clear Matte Laminate' },
    { value: '174', option: 'Clear Recyclable Matte Laminate' },
    { value: '5', option: 'Kraft Laminate' },
    { value: '6', option: 'Gloss Laminate With Metallic Effects' },
    { value: '7', option: 'Matte Laminate With Metallic Effects' },
    { value: '8', option: 'Gloss Holographic Laminate' },
    { value: '9', option: 'Matte Holographic Laminate' },
    { value: '65', option: 'Tear Resistant Gloss Laminate' },
    { value: '66', option: 'Tear Resistant Matte Laminate' },
];

const unwinds = {
    '1': {
        label: '1',
        imageUrl: '/images/unwind/unwind1.png',
        value: 1,
    },
    '2': {
        label: '2',
        imageUrl: '/images/unwind/unwind2.png',
        value: 2,
    },
    '3': {
        label: '3',
        imageUrl: '/images/unwind/unwind3.png',
        value: 3,
    },
    '4': {
        label: '4',
        imageUrl: '/images/unwind/unwind4.png',
        value: 4,
    },
    '5': {
        label: '5',
        imageUrl: '/images/unwind/unwind5.png',
        value: 5,
    },
    '6': {
        label: '6',
        imageUrl: '/images/unwind/unwind6.png',
        value: 6,
    },
    '7': {
        label: '7',
        imageUrl: '/images/unwind/unwind7.png',
        value: 7,
    },
    '8': {
        label: '8',
        imageUrl: '/images/unwind/unwind8.png',
        value: 8,
    },
};

const state = reactive({
    width: '',
    materialId: null,
    length: null,
    impressions: null,
    unwind: null,
    packagingComments: null,
    company: null,
    zipCode: null,
    firstName: null,
    lastName: null,
    email: null,
    phone: null,
    comments: null,
    submitting: false,
    response: null,
    formErrors: {},
});

const onRollstockFormSubmit = async () => {
    if (!form.value || !form.value.validate()) {
        return;
    }

    const recaptchaToken =
        recaptchaEnabled && window.grecaptcha
            ? window.grecaptcha.getResponse()
            : undefined;

    submitting.value = true;
    state.formErrors = {};
    state.response = null;

    await useAuth()
        .apiRequest('POST', 'products/custom-printed-rollstock', {
            material_id: materialId.value,
            roll_width: width.value,
            impression_length: width.value,
            number_of_impressions: impressions.value,
            unwind: unwind.value,
            packaging_comments: packagingComments.value,
            company_name: company.value,
            zip_code: zipCode.value,
            first_name: firstName.value,
            last_name: lastName.value,
            email: email.value,
            phone: phone.value,
            comments: comments.value,
            recaptchaToken: recaptchaToken,
        })
        .then((response: any) => {
            state.formErrors = {};

            alert(
                'Your request has been submitted!',
                'We will get in touch you within 2 business days.',
            );

            sendDataLayerFormEvent('form_submit', 'Custom Printed Rollstock', 'custom-printed-rollstock');
        })
        .catch((error) => {
            if (recaptchaEnabled && window.grecaptcha) {
                window.grecaptcha.reset();
            }

            state.formErrors = getFormErrors(error.data);
            nextTick(() => {
                scrollToId('form-errors');
            });
        })
        .finally(() => {
            state.submitting = false;
        });
};

const onUnwindClick = (value) => {
    state.unwind = value;
};

const unwindDirectionError = computed(() => {
    if (state.formErrors) {
        const unwindErrors = state.formErrors['unwind'] || [];

        return unwindErrors.length > 0;
    }
    return false;
});

const {
    width,
    materialId,
    unwind,
    length,
    impressions,
    packagingComments,
    company,
    zipCode,
    firstName,
    lastName,
    email,
    phone,
    comments,
    submitting,
    response,
    formErrors,
} = toRefs(state);
</script>
